import * as ko from "knockout";
import * as moment from "moment";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import { ICustomerDocument } from "../data/documentLibraryData";
import * as TargetData from "../data/targetData";
import { IApplication } from "../interfaces/application";
import * as Data from "../interfaces/data";

// classes used to edit a single target
interface IDropDownObservable {
    ID: KnockoutObservable<number>;
    Name: KnockoutObservable<string>;
}

interface IListValueObservable {
    ID: number;
    Value: string;
    IsActive: boolean;
}

interface ICustomerDocumentObservable {
    ID: KnockoutObservable<number>;
    StorageKey: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    Description: KnockoutObservable<string>;
    FileName: KnockoutObservable<string>;
    TempFileKey: KnockoutObservable<string>;
}

interface ITargetLinkedCustomerDocumentObservable {
    TargetCustomerDocumentID: KnockoutObservable<number>;
    CustomerDocumentID: KnockoutObservable<number>;
    StorageKey: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    Description: KnockoutObservable<string>;
    FileName: KnockoutObservable<string>;
}

interface ITargetMasterFormItemObservable {
    ID: number;
    Value: KnockoutObservable<string>;
    TargetTemplateFormItemID: number;
    ControlTypeID: number;
    FormText: string;
    IsRequired: boolean;
    MaxLength: number;
    TempFileKey: KnockoutObservable<string>;
    TempFileName: KnockoutObservable<string>;
    TempFileExtension: KnockoutObservable<string>;
    ShowTextField: boolean;
    ShowPictureUpload: boolean;
    IsFieldMissing: KnockoutObservable<boolean>;
    InvalidDateTimeFormat: KnockoutObservable<boolean>;
    TemplateName: string;
    ListValues: KnockoutObservableArray<IListValueObservable>;
    TemplatePhraseID: number;
    TemplatePhrases: KnockoutObservableArray<TargetData.ITemplatePhraseDetail>;

}

interface ITargetViewModel {
    ID: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    HasAddressFields: KnockoutObservable<boolean>;
    HasCustomFields: KnockoutObservable<boolean>;
    AddressFormItems: KnockoutObservableArray<ITargetMasterFormItemObservable>;
    FormItems: KnockoutObservableArray<ITargetMasterFormItemObservable>;
    StateID: KnockoutObservable<number>;
    AppointmentLabel: KnockoutObservable<string>;
    CustomerDocumentDownloadUrl: KnockoutObservable<string>;
    States: KnockoutObservableArray<any>;
    AllCustomerDocuments: KnockoutObservableArray<ICustomerDocumentObservable>;
    LinkedCustomerDocuments: KnockoutObservableArray<ITargetLinkedCustomerDocumentObservable>;
    SelectedCustomerDocument: KnockoutObservable<ICustomerDocumentObservable>;
    UnselectedCustomerDocuments?: KnockoutComputed<ICustomerDocumentObservable[]>;
    URL: string;
    PictureURL: KnockoutObservable<string>;
    TemplatePhraseID: KnockoutObservable<number>;
    TemplatePhrases: KnockoutObservableArray<TargetData.ITemplatePhraseDetail>;
    ListValues: KnockoutObservableArray<IListValueObservable>;
    DateFormat: string;
    TimeFormat: string;
    toggleIsActive: () => void;
    addAppointment: () => void;
    linkDocument: () => void;
    removeLinkedDocument: (doc: ITargetLinkedCustomerDocumentObservable) => void;
    closeUploadImageModal: () => void;
    showUploadImageModal: (formItemIndex: number) => void;
    removeImage: (formItemIndex: number) => void;
    closeViewImageModal: () => void;
    openViewImageModal: (formItemIndex: number) => void;
    getImageURL: (formItemIndex: number) => string | null;

}

export class TargetEditControl {
    private container: JQuery;
    private viewModel: ITargetViewModel;
    private fieldsOnLoad: string = "";
    private uploadControl: kendo.ui.Upload;
    private targetTemplateURLName: string = "";
    // The item pictures are being uploaded for
    private currentUploadFormItem: ITargetMasterFormItemObservable;

    constructor() {
        const me: TargetEditControl = this;
        const app = Global.app();
        const customerSettings = app.userSession ? app.userSession.CustomerSettings : [];

        this.viewModel = {
            ID: ko.observable(0),
            IsActive: ko.observable(true),
            HasAddressFields: ko.observable(false),
            HasCustomFields: ko.observable(false),
            AddressFormItems: ko.observableArray(),
            FormItems: ko.observableArray<ITargetMasterFormItemObservable>(),
            StateID: ko.observable(0),
            AppointmentLabel: ko.observable(Constants.LABEL_APPOINTMENT),
            States: ko.observableArray(),
            AllCustomerDocuments: ko.observableArray(),
            LinkedCustomerDocuments: ko.observableArray(),
            CustomerDocumentDownloadUrl: ko.observable(app.webServiceBaseURL + "/api/DocumentLibrary/Download/"),
            SelectedCustomerDocument: ko.observable(),
            URL: Global.app().webServiceBaseURL,
            PictureURL: ko.observable<string>(null),
            TemplatePhraseID: ko.observable(0),
            TemplatePhrases: ko.observableArray(),
            ListValues: ko.observableArray(),
            DateFormat: UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT),
            TimeFormat: UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT),
            toggleIsActive(): void {
                let message: string;

                if (this.IsActive()) {
                    message = "Are you sure you want to make this target inactive?";
                } else {
                    message = "Are you sure you want to make this target active?";
                }
                UI.showConfirmDialog(
                    message,
                    () => {
                        me.toggleIsActive();
                    });
            },
            addAppointment(): void {
                Global.app().router().navigate("/TaskAdd?targetID=" + me.viewModel.ID(), true);
            },
            linkDocument: () => {
                if (this.viewModel.SelectedCustomerDocument()) {

                    const doc = this.viewModel.SelectedCustomerDocument();
                    const linkedDoc: ITargetLinkedCustomerDocumentObservable = {
                        TargetCustomerDocumentID: ko.observable(0),
                        CustomerDocumentID: ko.observable(doc.ID()),
                        Description: ko.observable(doc.Description()),
                        FileName: ko.observable(doc.FileName()),
                        IsActive: ko.observable(doc.IsActive()),
                        StorageKey: ko.observable(doc.StorageKey()),
                    };

                    this.viewModel.LinkedCustomerDocuments.push(linkedDoc);
                    this.viewModel.SelectedCustomerDocument(null);
                }
            },
            removeLinkedDocument: (doc: ITargetLinkedCustomerDocumentObservable) => {
                UI.showConfirmDialog("Are you sure you want to remove this document?", () => {
                    if (doc.TargetCustomerDocumentID() > 0) {
                        // already in the db, so soft-delete
                        doc.IsActive(false);
                    } else {
                        // not yet in the db so just remove
                        this.viewModel.LinkedCustomerDocuments.remove(doc);
                    }
                });
            },
            UnselectedCustomerDocuments: ko.pureComputed((): ICustomerDocumentObservable[] => {
                const selectedDocumentIDs: number[] = this.viewModel.LinkedCustomerDocuments().map((doc) => doc.CustomerDocumentID());
                return this.viewModel.AllCustomerDocuments().filter((doc) => {
                    return selectedDocumentIDs.indexOf(doc.ID()) < 0;
                });
            }),
            closeUploadImageModal: () => {
                me.container.find("div[data-id='uploadImageDialog']").modal("hide");
            },
            showUploadImageModal: (formItemIndex: number) => {
                me.currentUploadFormItem = me.viewModel.FormItems()[formItemIndex];
                me.uploadControl.clearAllFiles();
                me.container.find("div[data-id='uploadImageDialog']").modal("show");
                me.container.find(".k-upload-status-total").remove();
            },
            removeImage: (formItemIndex: number) => {
                const formItem = me.viewModel.FormItems()[formItemIndex];
                formItem.Value(null);
                formItem.TempFileKey(null);
                formItem.TempFileExtension(null);
                formItem.TempFileName(null);
            },
            closeViewImageModal: () => {
                me.container.find("div[data-id='viewImageFormDialog']").modal("hide");
            },
            openViewImageModal: (formItemIndex: number) => {
                me.viewModel.PictureURL(me.viewModel.getImageURL(formItemIndex));
                me.container.find("div[data-id='viewImageFormDialog']").modal("show");
            },
            getImageURL: (formItemIndex: number): string | null => {
                const formItem = me.viewModel.FormItems()[formItemIndex];
                const cacheBuster = `?v=${(new Date()).getTime().toString()}`;
                if (formItem.TempFileKey() && formItem.TempFileExtension()) {
                    return `${Global.app().webServiceBaseURL}/api/Target/GetTempImage/${formItem.TempFileName()}/${formItem.TempFileExtension()}${cacheBuster}`;
                } else if (formItem.Value()) {
                    return `${Global.app().webServiceBaseURL}/api/Target/GetImage/${formItem.ID}${cacheBuster}`;
                } else {
                    return null;
                }
            },
        };
    }

    public render(container: JQuery): JQueryPromise<any> {
        const me: TargetEditControl = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        me.container = container;

        app.templates().render("targeteditcontrol", "template_target_edit_control", this.container).done(() => {
            this.uploadControl = this.container.find("input[data-id='uploadImageControl']").kendoUpload({
                async: {
                    saveUrl: "save",
                    removeUrl: "remove",
                    autoUpload: true,
                },
                validation: {
                    allowedExtensions: [".jpg", ".jpeg", ".png"],
                },
                upload: this.onImageUpload.bind(this),
                success: this.onUploadSuccess.bind(this),
                multiple: false,
            }).data("kendoUpload");

            ko.applyBindings(this.viewModel, container[0]);

            deferred.resolve();
        });
        return deferred.promise();
    }

    public load(targetTemplateURLName: string, targetID?: number | null): JQueryPromise<any> {
        const me: TargetEditControl = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        if (!targetTemplateURLName) {
            // invalid parameters passed into this page
            deferred.reject();
            return deferred.promise();
        }

        this.targetTemplateURLName = targetTemplateURLName;
        if (targetID) {
            this.viewModel.ID(targetID);
        } else {
            this.viewModel.ID(null);
        }

        TargetData.GetTargetMaster(this.targetTemplateURLName, this.viewModel.ID())
            .done((response: Data.IResponse<TargetData.ITargetMaster>) => {
                if (response.Success) {
                    me.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });
        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = JSON.stringify(this.getTargetMasterFromViewModel());
        return fieldValues !== this.fieldsOnLoad;
    }

    public subTitle(): string {
        if (this.viewModel.HasAddressFields()) {
            return this.viewModel.AddressFormItems()[0].Value();
        } else if (this.viewModel.HasCustomFields()) {
            return this.viewModel.FormItems()[0].Value();
        } else {
            return "";
        }
    }

    public save(): JQueryPromise<TargetData.ITargetMaster> {
        const me: TargetEditControl = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        let waitContainer: JQuery;

        if (me.viewModel.AddressFormItems()) {
            me.viewModel.AddressFormItems().forEach((formItem) => {
                if (formItem.FormText === "State") {
                    formItem.Value(me.getStateName());
                }
            });
        }

        if (this.isValid()) {
            waitContainer = UI.showWaitIndicator(this.container);

            TargetData.Save(this.getTargetMasterFromViewModel())
                .done((response: Data.IResponse<TargetData.ITargetMaster>) => {
                    if (response.Success) {
                        UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                        me.updateViewModel(response.Payload);
                        deferred.resolve(response.Payload);
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });
        } else {
            deferred.reject();
        }

        return deferred.promise();
    }

    private getStateName(): string {
        const me: TargetEditControl = this;
        let name: string = "";

        me.viewModel.States().forEach((state: IDropDownObservable) => {
            if (state.ID() === me.viewModel.StateID()) {
                name = state.Name();
            }
        });
        return name;
    }

    private setStateID(stateName: string, states: Array<Data.ICodeModel<number>>): void {
        const me: TargetEditControl = this;

        states.forEach((state: Data.ICodeModel<number>) => {
            if (state.Name === stateName) {
                me.viewModel.StateID(state.ID);
            }
        });
    }

    private updateViewModel(targetMaster: TargetData.ITargetMaster): void {
        const me: TargetEditControl = this;

        this.viewModel.ID(targetMaster.ID);
        this.viewModel.IsActive(targetMaster.IsActive);
        this.viewModel.HasAddressFields(targetMaster.HasAddressFields);
        this.viewModel.HasCustomFields(targetMaster.HasCustomFields);
        this.viewModel.States([]);

        me.viewModel.AddressFormItems.removeAll();
        me.viewModel.FormItems.removeAll();

        targetMaster.FormItems.forEach((formItem: TargetData.ITargetMasterFormItem) => {
            me.viewModel.FormItems.push(me.createFormItemObservable(formItem));
        });

        if (targetMaster.States) {
            targetMaster.States.forEach((state: Data.ICodeModel<number>) => {
                const dropdownState: IDropDownObservable = {
                    ID: ko.observable(state.ID),
                    Name: ko.observable(state.Name),
                };

                me.viewModel.States.push(dropdownState);
            });
        }

        targetMaster.AddressFormItems.forEach((formItem: TargetData.ITargetMasterFormItem) => {
            me.viewModel.AddressFormItems.push(me.createFormItemObservable(formItem));

            if (formItem.FormText === "State") {
                me.setStateID(formItem.Value, targetMaster.States);
            }
        });

        this.viewModel.LinkedCustomerDocuments.removeAll();
        targetMaster.LinkedCustomerDocuments.forEach((doc) => {
            this.viewModel.LinkedCustomerDocuments.push(this.createLinkedCustomerDocumentObservable(doc));
        });

        this.viewModel.AllCustomerDocuments.removeAll();
        targetMaster.AllCustomerDocuments.forEach((doc) => {
            this.viewModel.AllCustomerDocuments.push(this.createCustomerDocumentObservable(doc));
        });

        // remember the fields value so we can determine if they have changed
        this.fieldsOnLoad = JSON.stringify(this.getTargetMasterFromViewModel());
    }

    private createCustomerDocumentObservable(doc: ICustomerDocument): ICustomerDocumentObservable {
        return {
            ID: ko.observable(doc.ID),
            Description: ko.observable(doc.Description),
            FileName: ko.observable(doc.FileName),
            IsActive: ko.observable(doc.IsActive),
            StorageKey: ko.observable(doc.StorageKey),
            TempFileKey: ko.observable(doc.TempFileKey),
        };
    }

    private createLinkedCustomerDocumentObservable(doc: TargetData.ITargetLinkedCustomerDocument): ITargetLinkedCustomerDocumentObservable {
        return {
            TargetCustomerDocumentID: ko.observable(doc.TargetCustomerDocumentID),
            CustomerDocumentID: ko.observable(doc.CustomerDocumentID),
            Description: ko.observable(doc.Description),
            FileName: ko.observable(doc.FileName),
            IsActive: ko.observable(doc.IsActive),
            StorageKey: ko.observable(doc.StorageKey),
        };
    }
    private createFormItemObservable(formItem: TargetData.ITargetMasterFormItem): ITargetMasterFormItemObservable {

        const observable: ITargetMasterFormItemObservable = {
            ID: formItem.ID,
            Value: ko.observable<string>(this.convertValueForLocalDisplay(formItem.Value, formItem.ControlTypeID)),
            TargetTemplateFormItemID: formItem.TargetTemplateFormItemID,
            ControlTypeID: formItem.ControlTypeID,
            FormText: formItem.FormText,
            IsRequired: formItem.IsRequired,
            MaxLength: formItem.MaxLength,
            TempFileKey: ko.observable<string>(null),
            TempFileName: ko.observable<string>(null),
            TempFileExtension: ko.observable<string>(null),
            ShowTextField: formItem.ControlTypeID !== Constants.QuestionType.PICTURE,
            ShowPictureUpload: formItem.ControlTypeID === Constants.QuestionType.PICTURE,
            IsFieldMissing: ko.observable<boolean>(false),
            InvalidDateTimeFormat: ko.observable<boolean>(false),
            TemplateName: this.getTemplateNameFromControlTypeID(formItem.ControlTypeID),
            TemplatePhraseID: formItem.TemplatePhraseID,
            ListValues: ko.observableArray(formItem.ListValues),
            TemplatePhrases: ko.observableArray(formItem.TemplatePhrases),
        };
        return observable;
    }

    private isValid(): boolean {
        let validDateFormat: boolean = true;
        let isValid: boolean = true;
        const formItems = this.viewModel.HasCustomFields() ? this.viewModel.FormItems() : this.viewModel.AddressFormItems();
        const app: IApplication = Global.app();
        const customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
        const dateFormat = UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT);
        const timeFormat = UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT);

        for (const formItem of formItems) {
            if (formItem.IsRequired) {
                if (formItem.ShowPictureUpload) {
                    // For picture controls, Value is not going to be filled in until
                    // the record is actually saved.
                    if (!formItem.Value() && !formItem.TempFileKey()) {
                        isValid = false;
                        formItem.IsFieldMissing(true);
                    } else {
                        formItem.IsFieldMissing(false);
                    }
                } else {
                    isValid = UI.setRequiredString(formItem.Value, formItem.IsFieldMissing) ? isValid : false;
                }
            }
            switch (formItem.ControlTypeID) {
                case Constants.QuestionType.DATE:
                    validDateFormat = UI.isDateTimeValid(formItem.Value(), null, null, dateFormat, timeFormat);
                    isValid = validDateFormat ? isValid : false;
                    formItem.InvalidDateTimeFormat(!validDateFormat);
                    break;
                case Constants.QuestionType.DATETIME:
                    validDateFormat = UI.isDateTimeValid(null, null, formItem.Value(), dateFormat, timeFormat);
                    isValid =  validDateFormat ? isValid : false;
                    formItem.InvalidDateTimeFormat(!validDateFormat);
                    break;
                case Constants.QuestionType.TIME:
                    validDateFormat = UI.isDateTimeValid( null, formItem.Value(), null, dateFormat, timeFormat);
                    isValid = validDateFormat ? isValid : false;
                    formItem.InvalidDateTimeFormat(!validDateFormat);
                    break;
            }
        }

        if (!isValid) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
        }

        return isValid;
    }

    private toggleIsActive(): JQueryPromise<any> {
        const me: TargetEditControl = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
        const newIsActive: boolean = !this.viewModel.IsActive();
        const request: TargetData.ITargetSetIsActiveRequest = {
            TargetID: this.viewModel.ID(),
            IsActive: newIsActive,
        };

        TargetData.SetIsActive(request)
            .done((response: Data.IResponse<string>) => {
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                    me.viewModel.IsActive(newIsActive);

                    // remember the fields value so we can determine if they have changed
                    this.fieldsOnLoad = JSON.stringify(this.getTargetMasterFromViewModel());
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve(true);
            });

        return deferred.promise();
    }

    private onImageUpload(e: kendo.ui.UploadUploadEvent): void {
        const url: string = Global.app().webServiceBaseURL;

        if (this.uploadControl.options.async) {
            this.uploadControl.options.async.saveUrl = url + "/api/Target/UploadImage";
        }
    }

    private onUploadSuccess(e: kendo.ui.UploadSuccessEvent): void {
        if (this.currentUploadFormItem != null) {
            this.currentUploadFormItem.TempFileKey(e.response.TempFileKey);
            this.currentUploadFormItem.TempFileName(e.response.TempFileName);
            this.currentUploadFormItem.TempFileExtension(e.response.TempFileExtension);
        }
    }

    private getTargetMasterFromViewModel(): TargetData.ITargetMaster {
        const targetMaster: TargetData.ITargetMaster = {
            ID: this.viewModel.ID(),
            TargetTemplateURLName: this.targetTemplateURLName,
            AddressFormItems: this.getTargetMasterFormItemsFromObservable(this.viewModel.AddressFormItems),
            FormItems: this.getTargetMasterFormItemsFromObservable(this.viewModel.FormItems),
            LinkedCustomerDocuments: this.getTargetLinkedCustomDocumentsFromObservable(this.viewModel.LinkedCustomerDocuments),
            States: [],
            AllCustomerDocuments: [],
            IsActive: this.viewModel.IsActive(),
            HasAddressFields: this.viewModel.HasAddressFields(),
            HasCustomFields: this.viewModel.HasCustomFields(),
        };

        return targetMaster;
    }

    private getTargetMasterFormItemsFromObservable(formItemsObservable: KnockoutObservableArray<ITargetMasterFormItemObservable>): TargetData.ITargetMasterFormItem[] {
        const formItems: TargetData.ITargetMasterFormItem[] = [];
        for (const formItemObservable of formItemsObservable()) {

            formItems.push({
                ID: formItemObservable.ID,
                Value: this.convertValueForSave(formItemObservable.Value(), formItemObservable.ControlTypeID),
                TargetTemplateFormItemID: formItemObservable.TargetTemplateFormItemID,
                TempFileKey: formItemObservable.TempFileKey(),
                ControlTypeID: formItemObservable.ControlTypeID,
                FormText: formItemObservable.FormText,
                IsRequired: formItemObservable.IsRequired,
                MaxLength: formItemObservable.MaxLength,
                TemplatePhraseID: formItemObservable.TemplatePhraseID,
                ListValues: [],
                TemplatePhrases: [],
            });
        }
        return formItems;
    }

    private getTargetLinkedCustomDocumentsFromObservable(linkedDocumentsObservable: KnockoutObservableArray<ITargetLinkedCustomerDocumentObservable>):
                 TargetData.ITargetLinkedCustomerDocument[] {
        const documents: TargetData.ITargetLinkedCustomerDocument[] = [];
        for (const linkedDocumentObservable of linkedDocumentsObservable()) {
            documents.push({
                TargetCustomerDocumentID: linkedDocumentObservable.TargetCustomerDocumentID(),
                CustomerDocumentID: linkedDocumentObservable.CustomerDocumentID(),
                StorageKey: linkedDocumentObservable.StorageKey(),
                IsActive: linkedDocumentObservable.IsActive(),
                Description: linkedDocumentObservable.Description(),
                FileName: linkedDocumentObservable.FileName(),
            });
        }
        return documents;
    }

    private getTemplateNameFromControlTypeID(controlTypeID: number): string {
        switch (controlTypeID) {
            case Constants.QuestionType.PICTURE:
                return "target_edit_control_picture";
            case Constants.QuestionType.DATE:
                    return "target_edit_control_date";
            case Constants.QuestionType.DATETIME:
                    return "target_edit_control_date_time";
            case Constants.QuestionType.TIME:
                    return "target_edit_control_time";
            case Constants.QuestionType.TEXTAREA:
                    return "target_edit_control_text_area";
            case Constants.QuestionType.EMAIL:
                    return "target_edit_control_email";
            case Constants.QuestionType.PHONE:
                    return "target_edit_control_phone";
            case Constants.QuestionType.ADDRESS:
                    return "target_edit_control_address";
            case Constants.QuestionType.RADIOBUTTONLIST:
                return "target_edit_control_radio_button_list";
            case Constants.QuestionType.DROPDOWN:
                return "target_edit_control_dropdown";
            default:
                return "target_edit_control_text_box";
        }
    }

    // Converts the standard date/time formats into the formats used by the customer
    private convertValueForLocalDisplay(value: string, controlTypeID: number): string {
        const timeFormat: string = UI.getMomentTimeFormat(this.viewModel.TimeFormat);
        const dateFormat: string = UI.getMomentDateFormat(this.viewModel.DateFormat);

        let finalValue = value;
        if (value) {
            switch (controlTypeID) {
                case Constants.QuestionType.TIME:
                    finalValue = moment(value, Constants.MOMENT_FORMAT_TIME_STANDARDIZED).format(timeFormat);
                    break;
                case Constants.QuestionType.DATE:
                    finalValue = moment(value, Constants.MOMENT_FORMAT_DATE_STANDARDIZED).format(dateFormat);
                    break;
                case Constants.QuestionType.DATETIME:
                    finalValue = moment(value).local().format(`${dateFormat} ${timeFormat}`);
                    break;
                }
        }

        return finalValue;
    }

    private convertValueForSave(value: string, controlTypeID: number): string {
        let finalValue = value;
        if (value) {
            // Convert Date/Time values from customer formats into standard formats
            const timeFormat: string = UI.getMomentTimeFormat(this.viewModel.TimeFormat);
            const dateFormat: string = UI.getMomentDateFormat(this.viewModel.DateFormat);

            switch (controlTypeID) {
                case Constants.QuestionType.TIME:
                    finalValue = moment(value, timeFormat).format(Constants.MOMENT_FORMAT_TIME_STANDARDIZED);
                    break;
                case Constants.QuestionType.DATE:
                    finalValue = moment(value, dateFormat).format(Constants.MOMENT_FORMAT_DATE_STANDARDIZED);
                    break;
                case Constants.QuestionType.DATETIME:
                    finalValue = moment(value, `${dateFormat} ${timeFormat}`).utc()
                                        .format(`${Constants.MOMENT_FORMAT_DATE_STANDARDIZED}[T]${Constants.MOMENT_FORMAT_TIME_STANDARDIZED}[Z]`);
                    break;
            }
        }
        return finalValue;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/targetEdit.ts